import React from 'react';
import { Validator } from '@leverege/validator';
import { Config } from '@leverege/plugin';
import { Popup, TextInput, Flex, Text, Toast, Label, Selector } from '@leverege/ui-elements';
import { ApiProvider } from '@leverege/api-redux';
import I18N from '@leverege/i18n';
import Rsrc from "../Rsrc.js";
import Util from "./Util.js";
import S from './User.css';
const {
  t
} = I18N.ns('userManagement.user.UserSelector');

/**
 * This will allow the user to enter an email addrees and it will
 * request all users in the project with that email. If more than
 * one is supplied, a list to pick the user will be given. If only
 * one is present, that user will be selected. On a selection of
 * a user, the onChange() method will be called with an event
 * containing { value : <user>, data: props.eventData }.
 * 
 * This requires <ApiProvider> to be installed somewhere above
 * this React component.
 */
export default class UserSelector extends React.Component {
  /* Initialize static variables */
  static contextType = ApiProvider.Context;

  /**
   * Constructs component.
   */
  constructor(props) {
    super(props);
    this.state = {
      users: null,
      apiAccesses: null,
      loading: false,
      dataRequested: false
    };
  }
  get allowNewUsers() {
    const {
      canCreateUsers,
      allowNewUsers
    } = this.props;
    if (!canCreateUsers || !allowNewUsers) {
      return false;
    }
    return true;
  }

  /**
   * Runs once when component mounts.
   */
  componentDidMount() {
    const {
      allowApiAccess
    } = this.props;
    this.getOidcClients();
    if (allowApiAccess) {
      this.requestApiAccesses();
    }
  }

  /**
   * Parses text input for email / username field.
   */
  parseInput(value) {
    const v = (value || '').trim();
    if (Validator.isEmail(v)) {
      return {
        value: v,
        email: v,
        username: null,
        error: false
      };
    }
    if (v.length > 3 && /^[a-zA-Z0-9_\-.]+$/.test(v)) {
      return {
        value: v,
        email: null,
        username: v,
        error: false
      };
    }
    return {
      value: v,
      email: null,
      username: null,
      error: true,
      errorMessage: t('invalidEmail')
    };
  }

  /**
   * Gets oidc clients. If clients don't exist in state, fetches
   * from api then updates state and returns clients. 
   */
  async getOidcClients() {
    // Get parameters
    const {
      oidcClients: prevOidcClients
    } = this.state;
    const {
      api: pApi
    } = this.props;
    const api = pApi || this.context;
    const {
      projectId
    } = this.props;

    // Check if already retrieved oidc clients
    if (prevOidcClients != null) {
      return prevOidcClients;
    }

    // Get oidc clients from api
    let oidcClients = [];
    try {
      const oidcData = await api.project(projectId || api.project).oidcClients().list();
      oidcClients = oidcData.items;
    } catch (error) {
      console.log('Unable to return oidc clients', error);
    }
    if (Array.isArray(oidcClients)) {
      oidcClients = Util.filterOidcClients(oidcClients, Config.get('ResourceUsers', 'oidcClientFilter'));
    }

    // Update state and return clients
    this.setState({
      oidcClients
    });
    return oidcClients;
  }

  /**
   * Requests users from api.
   */
  requestUsers = async () => {
    // Get parameters
    const {
      projectId,
      resourceId,
      api: pApi
    } = this.props;
    const {
      value
    } = this.state;
    const api = pApi || this.context;
    // Parse input and update state
    const parse = this.parseInput(value);
    this.setState({
      users: null,
      ...parse,
      loading: parse.error === false,
      dataRequested: true
    });

    // Check for parsing errors
    if (parse.error !== false) {
      return;
    }

    // Request users from api
    try {
      const data = await api.project(projectId || api.project).accounts().list({
        [parse.email ? 'email' : 'username']: parse.value,
        resourceId
      });
      this.onReceiveUsers(data);
    } catch (error) {
      Toast.error([t('userLookupError'), error.message]);
    }
  };
  requestApiAccesses = async () => {
    const {
      projectId,
      api: pApi
    } = this.props;
    const api = pApi || this.context;
    try {
      this.setState({
        loading: true
      });
      const res = await api.project(projectId).apiAccessKeys().list();
      this.setState({
        apiAccesses: res.items,
        loading: false
      });
    } catch (err) {
      Toast.error([t('apiaccessLookupError'), err.message]);
    }
  };

  /**
   * Handles user input events.
   */
  onUserInput = event => {
    if (event.causedByBlur === true) {
      this.requestUsers();
    } else {
      this.setState({
        value: event.value,
        selected: null,
        users: null,
        loading: false,
        email: null,
        username: null,
        error: false
      });
      if (this.popupControl.isOpened) {
        this.popupControl.setOpened(false);
      }
    }
  };

  /**
   * Handles focus events.
   */
  onFocus = event => {
    const {
      users
    } = this.state;
    if (users && users.length > 1) {
      this.popupControl.setOpened(true);
    }
  };

  /**
   * Handlers key down events.
   */
  onKeyDown = event => {
    const {
      selected
    } = this.state;
    const pressedReturn = event.which === 13 || event.keyCode === 13;
    if (pressedReturn) {
      this.requestUsers();
    } else if (selected) {
      this.onSelectUser({
        data: null
      });
    }
    return true;
  };

  /**
   * Handles user selection events.
   */
  onSelectUser = event => {
    // Get parameters
    const {
      onChange,
      eventData
    } = this.props;

    // Send event to callback prop if defined
    if (typeof onChange === 'function') {
      onChange({
        data: eventData,
        value: event.data
      });
    }

    // Update state with event data and clear existing users
    this.setState({
      selected: event.data,
      users: null,
      loading: false
    });
  };
  onSelectApiAccess = event => {
    const {
      onChange
    } = this.props;
    if (typeof onChange === 'function') {
      onChange(event);
    }
    this.setState({
      selected: event.data,
      apiAccesses: null,
      loading: false
    });
  };

  /**
   * Handles data received from get user requests.
   */
  onReceiveUsers = async data => {
    // Get parameters
    const users = data.items;
    const {
      acceptOne = false,
      setExistingUsers
    } = this.props;
    const {
      email
    } = this.state;
    const oidcClients = await this.getOidcClients();

    // Send existing users to callback prop if defined
    if (typeof setExistingUsers === 'function') {
      setExistingUsers(users);
    }

    // Check if immediately accepting user
    const immediatelyAccept = users.length === 1 && (acceptOne || email == null || oidcClients.length <= 1);
    if (immediatelyAccept) {
      this.popupControl.setOpened(false);
      return this.onSelectUser({
        data: {
          ...users[0],
          selectionType: 'update'
        }
      });
    }

    // Let user select option from popup
    this.popupControl.setOpened(true);
    this.setState({
      users,
      loading: false
    });
  };

  /**
   * Gets new user items.
   */
  getNewUserItems({
    email,
    username,
    style,
    onClose
  }) {
    if (username === null) {
      username = email;
    }
    if (!Validator.isEmail(email)) {
      email = null;
    }

    // Check if user can create other users
    if (!this.allowNewUsers || !Rsrc.allowCreateUser && !Rsrc.allowInviteUser) {
      return [];
    }

    // Create and return new user items
    return [/*#__PURE__*/React.createElement(Popup.Header, {
      key: "newUsers"
    }, t('newUser')), Rsrc.allowInviteUser && /*#__PURE__*/React.createElement(Popup.Item, {
      key: "inviteNewUser",
      onClick: this.onSelectUser,
      onClose: onClose,
      eventData: {
        username,
        email,
        phone: null,
        password: null,
        selectionType: 'invite'
      },
      style: style
    }, t('invite')), Rsrc.allowCreateUser && /*#__PURE__*/React.createElement(Popup.Item, {
      key: "createNewUser",
      onClick: this.onSelectUser,
      onClose: onClose,
      eventData: {
        username,
        email,
        phone: null,
        password: null,
        selectionType: 'create'
      },
      style: style
    }, t('create'))];
  }

  /**
   * Gets external user items.
   */
  getExternalUserItems({
    user,
    users,
    oidcClients,
    email,
    style,
    onClose
  }) {
    const {
      allowExternalUsers
    } = this.props;
    // Check if external users are allowed
    if (!allowExternalUsers) {
      return [];
    }

    // Get paramters
    const clientIsAvailable = client => users.find(user => client.clientId === user?.oidc?.clientId) == null;
    const availableClients = Array.isArray(oidcClients) ? oidcClients.filter(client => clientIsAvailable(client)) : [];

    // Only allow oidc access if email provided
    if (email == null) {
      return [];
    }

    // Check for available clients
    if (availableClients.length < 1) {
      return [];
    }

    // Add header
    const items = [/*#__PURE__*/React.createElement(Popup.Header, {
      key: "importableUsers"
    }, t('externalUser'))];

    // Add available clients
    availableClients.forEach(client => {
      items.push(/*#__PURE__*/React.createElement(Popup.Item, {
        eventData: {
          email,
          oidcClientId: client.id,
          selectionType: 'oidcAccess'
        },
        onClick: this.onSelectUser,
        onClose: onClose,
        key: client.id,
        style: style
      }, t('externalUserAccess', {
        client: client.name
      })));
    });

    // Return items
    return items;
  }

  /**
   * Gets existing user items.
   */
  getExistingUserItems({
    users,
    oidcClients,
    style,
    onClose
  }) {
    // Get parameters
    const {
      allowExternalUsers
    } = this.props;
    const allowNewUsers = this.allowNewUsers;
    const items = [];

    // Check for existing users
    if (!Array.isArray(users) || users.length < 1) {
      return allowNewUsers || allowExternalUsers ? [] : [/*#__PURE__*/React.createElement(Popup.Item, {
        key: "noUsersFound",
        style: style,
        onClose: onClose
      }, /*#__PURE__*/React.createElement("i", null, t('noUsersFound')))];
    }

    // Check if header is necessary
    if (allowNewUsers || allowExternalUsers) {
      items.push(/*#__PURE__*/React.createElement(Popup.Header, {
        key: "existingUsers"
      }, t('existingUsers')));
    }

    // Add existing users
    users.map(user => {
      const name = user.username || user.name || user?.oidc?.userInfo?.name || user.email;
      const oidcClient = user?.oidc ? oidcClients.find(item => item.clientId === user?.oidc?.clientId) : null;
      const source = oidcClient?.name || '';
      items.push(/*#__PURE__*/React.createElement(Popup.Item, {
        style: style,
        key: user.id,
        eventData: {
          ...user,
          selectionType: 'update'
        },
        onClick: this.onSelectUser,
        onClose: onClose
      }, /*#__PURE__*/React.createElement(Flex, {
        variant: "rowM",
        justify: "space-between"
      }, /*#__PURE__*/React.createElement(Text, null, name), /*#__PURE__*/React.createElement(Text, {
        variant: "caption"
      }, source))));
    });

    // Return items
    return items;
  }

  /**
   * Renders items for selection.
   */
  renderItems = onClose => {
    // Get parameters
    const {
      additionalUserItems = []
    } = this.props;
    const {
      users,
      oidcClients = [],
      email,
      username
    } = this.state;
    const elem = this.popupControl.ref?.current;
    const style = elem && typeof elem.clientWidth === 'number' ? {
      minWidth: `${elem.clientWidth}px`
    } : null;

    // Validate parameters
    if (users === null) {
      return null;
    }

    // Get and return items
    const newUserItems = this.getNewUserItems({
      email,
      username,
      style,
      onClose
    });
    const existingUserItems = this.getExistingUserItems({
      users,
      oidcClients,
      style,
      onClose
    });
    const externalUserItems = this.getExternalUserItems({
      users,
      oidcClients,
      email,
      style,
      onClose
    });
    return [...newUserItems, ...existingUserItems, ...externalUserItems, ...additionalUserItems];
  };

  /**
   * Gets selector subtitle. Right now only needed for oidc to show that a user is signing
   * in from an oidc client. However for pending users we do now know the client name so
   * need to use a generic `Pending OIDC Client` name.
   */
  getSubtitle = () => {
    const {
      oidcClients
    } = this.state;
    const {
      user
    } = this.props;
    const userOidcClientId = user?.oidcClientId || user?.oidc?.clientId;
    const oidcClient = oidcClients?.find(item => item.id === userOidcClientId);
    const oidcClientName = oidcClient?.name || userOidcClientId ? t('pendingOIDCClient') : null;
    return oidcClientName ? t('userFromClient', {
      client: oidcClientName
    }) : null;
  };

  /**
   * Renders component.
   */
  render() {
    // Get parameters
    const {
      size = 48,
      eventData,
      hint = Rsrc.usernameHint,
      user,
      roleSelector,
      variants,
      requireEmail = false,
      requireUsername = false,
      allowApiAccess
    } = this.props;
    const {
      loading,
      value,
      error,
      errorMessage,
      dataRequested,
      apiAccesses
    } = this.state;
    const showInstructions = !value || value?.length < 4 || !dataRequested;
    const caption = error ? errorMessage : showInstructions ? Rsrc.usernameInstructions : null;
    const subtitle = this.getSubtitle();
    const options = apiAccesses?.map(apiAccess => {
      return {
        name: apiAccess.name,
        value: {
          ...apiAccess,
          selectionType: 'apiAccess'
        }
      };
    }) || [];

    // Return component
    return /*#__PURE__*/React.createElement("div", {
      className: S.gridContainer
    }, subtitle && /*#__PURE__*/React.createElement(Text, {
      variant: variants?.selectionSubtitle
    }, subtitle), !requireEmail && !requireUsername && /*#__PURE__*/React.createElement("div", {
      className: S.gridItem
    }, /*#__PURE__*/React.createElement(Label, {
      variant: variants?.inputLabel
    }, Rsrc.usernameLabel), /*#__PURE__*/React.createElement(Popup, {
      key: "userSelectorPopup",
      render: this.renderItems,
      trigger: popupControl => {
        this.popupControl = popupControl;
        return /*#__PURE__*/React.createElement(TextInput, {
          variant: variants?.inputText,
          hint: hint,
          autoFocus: true,
          "data-error": error,
          onFocus: this.onFocus,
          value: loading ? t('loading') : value,
          disabled: loading,
          size: size,
          domRef: popupControl.ref,
          key: "input",
          alwaysFireOnReturn: false,
          alwaysFireOnBlur: true,
          changeOnReturn: false,
          changeOnBlur: true,
          onKeyDown: this.onKeyDown,
          onChange: this.onUserInput,
          eventData: popupControl
        });
      }
    }), caption && /*#__PURE__*/React.createElement(Text, {
      variant: variants?.inputCaption
    }, caption)), requireUsername && /*#__PURE__*/React.createElement("div", {
      className: S.gridItem
    }, /*#__PURE__*/React.createElement(Label, {
      variant: variants?.inputLabel
    }, Rsrc.usernameLabel), /*#__PURE__*/React.createElement(Text, {
      variant: variants?.inputCaption
    }, user?.username || user?.name || '-')), requireEmail && user?.email && /*#__PURE__*/React.createElement("div", {
      className: S.gridItem
    }, /*#__PURE__*/React.createElement(Label, {
      variant: variants?.inputLabel
    }, Rsrc.userSelectorEmailLabel), /*#__PURE__*/React.createElement(Text, {
      variant: variants?.inputCaption
    }, user?.email || '-')), /*#__PURE__*/React.isValidElement(roleSelector) && user != null && /*#__PURE__*/React.createElement("div", {
      className: S.gridItem
    }, roleSelector), allowApiAccess && /*#__PURE__*/React.createElement(Flex, {
      align: "center"
    }, /*#__PURE__*/React.createElement(Text, null, "or"), /*#__PURE__*/React.createElement(Selector, {
      eventData: eventData,
      variant: "apiAccess",
      showFilter: true,
      nameKey: "name",
      valueKey: "value",
      title: t('title'),
      values: options,
      onChange: this.onSelectApiAccess
    })));
  }
}